import { useState, useEffect } from "react";
import {
  Send,
  ArrowDownCircle,
  ArrowUpCircle,
  CheckCircle,
  Users,
  Check,
  X,
  Wallet,
  DollarSign,
} from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { getBalance, USDC_ADDRESS } from "../services/authapi";
import { ethers } from "ethers";
import toast, { Toaster } from "react-hot-toast";
import { useSmartWallets } from "@privy-io/react-auth/smart-wallets";

interface User {
  uid: string;
  email: string | null;
  phone: string | null;
  wallet: string;
  username: string;
  joinedAt: string;
}

interface Recipient {
  id: string;
  username?: string;
  email?: string | null;
  phone?: string | null;
  wallet: string;
}

interface PaymentRequest {
  id: string;
  requesterUid: string;
  requestedUid: string;
  requesterUsername: string;
  requestedUsername: string;
  requesterWallet: string;
  requestedWallet: string;
  requesterEmail: string | null;
  requestedEmail: string | null;
  amount: number;
  status: "pending" | "completed" | "cancelled";
  timestamp: string;
}

const PaymentRequestPage = () => {
  const [activeTab, setActiveTab] = useState<
    "send" | "myRequests" | "received"
  >("send");
  const [amount, setAmount] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | any>(
    null
  );
  const [users, setUsers] = useState<Recipient[]>([]);
  const [filteredResults, setFilteredResults] = useState<Recipient[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [walletBalance, setWalletBalance] = useState<string>("0");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sentRequests, setSentRequests] = useState<PaymentRequest[]>([]);
  const [receivedRequests, setReceivedRequests] = useState<PaymentRequest[]>(
    []
  );
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedPayRequest, setSelectedPayRequest] =
    useState<PaymentRequest | null>(null);
  const { client } = useSmartWallets() as any;

  const user = useSelector((state: RootState) => state.user) as User | null;
  const senderWallet = user?.wallet || "";
  const senderUid = user?.uid || "";
  const senderUsername = user?.username || "User";
  const senderEmail = user?.email || null;

  const sendEmail = async (
    sender_email: string,
    receiver_email: string | null,
    sender_name: string,
    receiver_name: string,
    endpoint: string,
    params: { [key: string]: string }
  ) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SERVERURL}/${endpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            sender_email,
            receiver_email: receiver_email || "support@cashtap.cash",
            sender_name,
            receiver_name,
            params,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`${endpoint} email sent:`, data);
    } catch (err: any) {
      console.error(`Failed to send ${endpoint} email:`, err);
      toast.error(`Failed to send email: ${err.message}`);
    }
  };

  useEffect(() => {
    const fetchBalances = async () => {
      if (!senderWallet) {
        setError("No sender wallet address found.");
        toast.error("No sender wallet address found.");
        return;
      }

      setLoading(true);
      try {
        const usdcBal = await getBalance(senderWallet);
        setWalletBalance(usdcBal);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching balances:", err.message);
        toast.error("Failed to fetch balances.");
        setError("Failed to fetch balances: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBalances();
  }, [senderWallet]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          username: doc.data().username,
          email: doc.data().email || null,
          phone: doc.data().phone || null,
          wallet: doc.data().wallet,
        }));
        setUsers(usersData);
      } catch (err: any) {
        console.error("Error fetching users:", err);
        toast.error("Failed to fetch users.");
        setError("Failed to fetch users: " + err.message);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchRequests = async () => {
      if (!senderUid) return;

      try {
        // Fetch sent requests
        const sentQuery = query(
          collection(db, "paymentRequests"),
          where("requesterUid", "==", senderUid)
        );
        const sentSnapshot = await getDocs(sentQuery);
        const sentData = sentSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as PaymentRequest[];
        setSentRequests(sentData);

        // Fetch received requests
        const receivedQuery = query(
          collection(db, "paymentRequests"),
          where("requestedUid", "==", senderUid)
        );
        const receivedSnapshot = await getDocs(receivedQuery);
        const receivedData = receivedSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as PaymentRequest[];
        setReceivedRequests(receivedData);
      } catch (err: any) {
        console.error("Error fetching payment requests:", err);
        toast.error("Failed to fetch payment requests.");
      }
    };

    fetchRequests();
  }, [senderUid]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setSelectedRecipient(null);

    const searchTerm = value.toLowerCase();
    const filtered = users.filter(
      (item) =>
        (item.username?.toLowerCase().includes(searchTerm) ||
          item.email?.toLowerCase().includes(searchTerm) ||
          item.phone?.toLowerCase().includes(searchTerm) ||
          item.wallet.toLowerCase().includes(searchTerm)) &&
        item.id !== senderUid
    );

    setFilteredResults(filtered);
    setShowDropdown(value.length >= 2 && filtered.length > 0);
  };

  const handleSelectRecipient = (selected: Recipient) => {
    setSelectedRecipient(selected);
    setSearchQuery(selected.username || "");
    setShowDropdown(false);
  };

  const handleSendRequest = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedRecipient) {
      toast.error("Please select a recipient");
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (parsedAmount <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }

    try {
      setLoading(true);

      const requestDoc = await addDoc(collection(db, "paymentRequests"), {
        requesterUid: senderUid,
        requestedUid: selectedRecipient.id,
        requesterUsername: senderUsername,
        requestedUsername: selectedRecipient.username,
        requesterWallet: senderWallet,
        requestedWallet: selectedRecipient.wallet,
        requesterEmail: senderEmail,
        requestedEmail: selectedRecipient.email,
        amount: parsedAmount,
        status: "pending",
        timestamp: new Date().toISOString(),
      });

      // Send emails for request sent (template ID 7) and received (template ID 8)
      const requestParams = {
        FIRSTNAME: senderUsername,
        RECIPIENT: selectedRecipient.username || "CashTap User",
        AMOUNT: parsedAmount.toFixed(2),
        REQUEST_ID: requestDoc.id,
        DATE: new Date().toISOString().split("T")[0],
        EMAIL: senderEmail || "support@cashtap.cash",
      };

      await sendEmail(
        senderEmail || "support@cashtap.cash",
        selectedRecipient.email,
        senderUsername,
        selectedRecipient.username || "CashTap User",
        "send-payment-request",
        requestParams
      );

      toast.success("Payment request sent successfully!");
      setAmount("");
      setSearchQuery("");
      setSelectedRecipient(null);

      // Refresh requests
      const sentQuery = query(
        collection(db, "paymentRequests"),
        where("requesterUid", "==", senderUid)
      );
      const sentSnapshot = await getDocs(sentQuery);
      const sentData = sentSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as PaymentRequest[];
      setSentRequests(sentData);

      const receivedQuery = query(
        collection(db, "paymentRequests"),
        where("requestedUid", "==", senderUid)
      );
      const receivedSnapshot = await getDocs(receivedQuery);
      const receivedData = receivedSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as PaymentRequest[];
      setReceivedRequests(receivedData);
    } catch (err: any) {
      console.error("Error sending payment request:", err.message);
      toast.error("Failed to send payment request.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (request: PaymentRequest) => {
    try {
      setLoading(true);

      // Update request status to cancelled
      const requestRef = doc(db, "paymentRequests", request.id);
      await updateDoc(requestRef, { status: "cancelled" });

      // Send cancellation email (template ID 9) to both requester and recipient
      const cancelParams = {
        FIRSTNAME: request.requestedUsername,
        CANCELLED_BY: senderUsername,
        AMOUNT: request.amount.toFixed(2),
        REQUEST_ID: request.id,
        DATE: new Date().toISOString().split("T")[0],
        EMAIL: request.requestedEmail || "support@cashtap.cash",
      };

      await sendEmail(
        request.requesterEmail || "support@cashtap.cash",
        request.requestedEmail,
        request.requesterUsername,
        request.requestedUsername,
        "cancel-payment-request",
        cancelParams
      );

      toast.success("Payment request cancelled successfully!");

      // Refresh requests
      const sentQuery = query(
        collection(db, "paymentRequests"),
        where("requesterUid", "==", senderUid)
      );
      const sentSnapshot = await getDocs(sentQuery);
      const sentData = sentSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as PaymentRequest[];
      setSentRequests(sentData);

      const receivedQuery = query(
        collection(db, "paymentRequests"),
        where("requestedUid", "==", senderUid)
      );
      const receivedSnapshot = await getDocs(receivedQuery);
      const receivedData = receivedSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as PaymentRequest[];
      setReceivedRequests(receivedData);
    } catch (err: any) {
      console.error("Error cancelling payment request:", err.message);
      toast.error("Failed to cancel payment request.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPayModal = (request: PaymentRequest) => {
    const parsedAmount = request.amount;
    const fee = parsedAmount * 0.01;
    const totalAmount = parsedAmount + fee;
    if (totalAmount > parseFloat(walletBalance)) {
      toast.error("Insufficient USDC balance for amount + fee");
      return;
    }
    setSelectedPayRequest(request);
    setShowPayModal(true);
  };

  const confirmPayRequest = async () => {
    if (!selectedPayRequest) return;

    try {
      setLoading(true);

      // Construct USDC transfer data
      const amountWei = ethers.parseUnits(
        selectedPayRequest.amount.toString(),
        6
      ); // USDC has 6 decimals
      const feeWei = amountWei / 100n; // 1% fee
      const transferInterface = new ethers.Interface([
        "function transfer(address to, uint256 amount) returns (bool)",
      ]);

      const calls = [
        {
          to: USDC_ADDRESS,
          data: transferInterface.encodeFunctionData("transfer", [
            selectedPayRequest.requesterWallet,
            amountWei,
          ]),
        },
        {
          to: USDC_ADDRESS,
          data: transferInterface.encodeFunctionData("transfer", [
            import.meta.env.VITE_Admin_Wallet,
            feeWei,
          ]),
        },
      ];

      // Send transaction
      const txHash = await client.sendTransaction({
        chain: 8453,
        calls,
      });

      // Update request status
      const requestRef = doc(db, "paymentRequests", selectedPayRequest.id);
      await updateDoc(requestRef, { status: "completed" });

      // Send transfer emails
      const transactionDate = new Date().toISOString().split("T")[0];
      await sendEmail(
        selectedPayRequest.requestedEmail || "support@cashtap.cash",
        selectedPayRequest.requesterEmail,
        senderUsername,
        selectedPayRequest.requesterUsername,
        "complete-transfer",
        {
          AMOUNT: selectedPayRequest.amount.toFixed(2),
          SENDER: senderUsername,
          TRANSFERED_TO: selectedPayRequest.requesterUsername,
          TRANSACTION_ID: txHash,
          DATE: transactionDate,
        }
      );

      toast.success("Payment completed successfully!");

      // Refresh balance
      const usdcBal = await getBalance(senderWallet);
      setWalletBalance(usdcBal);

      // Refresh requests
      const receivedQuery = query(
        collection(db, "paymentRequests"),
        where("requestedUid", "==", senderUid)
      );
      const receivedSnapshot = await getDocs(receivedQuery);
      const receivedData = receivedSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as PaymentRequest[];
      setReceivedRequests(receivedData);

      setShowPayModal(false);
      setSelectedPayRequest(null);
    } catch (err: any) {
      console.error("Error completing payment:", err.message);
      let errorMessage = "Failed to complete payment: " + err.message;
      if (err.message.includes("sponsorship")) {
        errorMessage =
          "Gas sponsorship failed. Ensure sufficient credits in Privy dashboard.";
      } else if (err.message.includes("insufficient funds")) {
        errorMessage = "Insufficient USDC balance or sponsorship credits.";
      } else if (err.message.includes("wallet")) {
        errorMessage =
          "Wallet error. Ensure you're using a Privy embedded wallet.";
      }
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const truncateAddress = (address: string): string => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4 sm:p-0">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#0F1419",
            color: "#fff",
            border: "1px solid #374151",
            borderRadius: "12px",
            fontSize: "14px",
          },
          success: {
            iconTheme: {
              primary: "#61CE70",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#EF4444",
              secondary: "#fff",
            },
          },
        }}
      />
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
          Payment Requests
        </h1>
        <p className="text-gray-400 text-lg">Manage your payment requests</p>
      </div>

      {/* Tab Switcher */}
      <div className="flex space-x-2 mb-6">
        <button
          onClick={() => setActiveTab("send")}
          className={`px-4 py-2 rounded-xl font-medium text-sm sm:text-base transition-all duration-300 ${
            activeTab === "send"
              ? "bg-[#61CE70] text-white shadow-md shadow-[#61CE70]/30"
              : "bg-gray-800/50 text-gray-400 hover:bg-gray-700/50"
          }`}
        >
          Send Request
        </button>
        <button
          onClick={() => setActiveTab("myRequests")}
          className={`px-4 py-2 rounded-xl font-medium text-sm sm:text-base transition-all duration-300 ${
            activeTab === "myRequests"
              ? "bg-[#61CE70] text-white shadow-md shadow-[#61CE70]/30"
              : "bg-gray-800/50 text-gray-400 hover:bg-gray-700/50"
          }`}
        >
          My Requests
        </button>
        <button
          onClick={() => setActiveTab("received")}
          className={`px-4 py-2 rounded-xl font-medium text-sm sm:text-base transition-all duration-300 ${
            activeTab === "received"
              ? "bg-[#61CE70] text-white shadow-md shadow-[#61CE70]/30"
              : "bg-gray-800/50 text-gray-400 hover:bg-gray-700/50"
          }`}
        >
          Received Requests
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-2xl">
          {error}
        </div>
      )}

      {activeTab === "send" && (
        <form onSubmit={handleSendRequest} className="space-y-6">
          {/* Select Recipient */}
          <div className="bg-gradient-to-br z-20 from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm relative">
            <h3 className="text-xl font-bold text-white mb-4">
              Select User to Request From
            </h3>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full px-4 py-4 bg-gray-800/30 border border-gray-700/50 rounded-2xl text-white placeholder-gray-400 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all font-mono"
                placeholder="Enter CashTapTag, Phone, Email"
                required
              />
              {showDropdown && (
                <div className="absolute w-full bg-gradient-to-br from-[#1A2A3B] to-[#0D1419] border border-gray-700/50 rounded-3xl shadow-[0_0_10px_rgba(97,206,112,0.2)] transition-all duration-200 overflow-hidden top-full mt-2 max-h-60 overflow-y-auto">
                  {filteredResults.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleSelectRecipient(item)}
                      className="p-4 hover:bg-[#61CE70]/5 cursor-pointer flex items-center justify-between border-b border-gray-700/50 last:border-b-0 transition-colors duration-200"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-[#61CE70]/20 rounded-lg">
                          <Users className="w-4 h-4 text-[#61CE70]" />
                        </div>
                        <div>
                          <span className="text-white font-medium">
                            {item.username}
                          </span>
                          <p className="text-gray-400 text-xs">
                            {item.email || item.phone || "No contact info"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-gray-400 text-sm">
                          Wallet: {truncateAddress(item.wallet)}
                        </span>
                        {selectedRecipient?.id === item.id && (
                          <Check className="w-5 h-5 text-[#61CE70]" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {selectedRecipient && (
                <div className="mt-2 p-3 bg-gray-800/30 rounded-xl border border-[#61CE70]/20 flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-[#61CE70]" />
                  <p className="text-gray-300 text-sm">
                    Recipient Wallet:{" "}
                    <span className="font-mono text-white">
                      {truncateAddress(selectedRecipient.wallet)}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Amount */}
          <div className="bg-gradient-to-br -z-20 from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
            <h3 className="text-xl font-bold text-white mb-4">Amount</h3>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-4 py-4 bg-gray-800/30 border border-gray-700/50 rounded-2xl text-white text-lg placeholder-gray-400 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
                placeholder="0.0"
                min="0.1"
                step="0.1"
                required
              />
              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg">
                USDC
              </span>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!amount || !selectedRecipient || loading}
            className="w-full bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white py-4 rounded-2xl font-medium hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none flex items-center justify-center space-x-2"
          >
            <Send className="w-5 h-5" />
            <span>{loading ? "Sending..." : "Send Payment Request"}</span>
          </button>
        </form>
      )}

      {activeTab === "myRequests" && (
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
          <h3 className="text-xl font-bold text-white mb-6">
            My Payment Requests
          </h3>
          {sentRequests.length === 0 ? (
            <div className="text-center text-gray-400">
              No sent requests found.
            </div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {sentRequests.map((req) => (
                <div
                  key={req.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 bg-gray-800/30 rounded-2xl hover:bg-gray-800/50 transition-colors"
                >
                  <div className="flex items-start sm:items-center space-x-4 w-full sm:w-auto">
                    <div className="p-3 rounded-xl shrink-0 bg-[#61CE70]/20">
                      <ArrowUpCircle className="w-5 h-5 text-[#61CE70]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium">
                        To: {req.requestedUsername}
                      </p>
                      <p className="text-gray-400 text-sm">
                        Amount: {req.amount.toFixed(2)} USDC
                      </p>
                      <p className="text-gray-500 text-xs">{req.timestamp}</p>
                    </div>
                  </div>
                  <div className="mt-4 sm:mt-0 text-left sm:text-right space-y-2 w-full sm:w-auto">
                    <p
                      className={`text-base sm:text-lg font-bold ${
                        req.status === "completed"
                          ? "text-[#61CE70]"
                          : req.status === "cancelled"
                          ? "text-red-400"
                          : "text-yellow-400"
                      }`}
                    >
                      {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                    </p>
                    {req.status === "pending" && (
                      <button
                        onClick={() => handleCancelRequest(req)}
                        disabled={loading}
                        className="bg-red-600 text-white px-4 py-2 rounded-xl font-medium hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? "Cancelling..." : "Cancel Request"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "received" && (
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
          <h3 className="text-xl font-bold text-white mb-6">
            Payment Requests Received
          </h3>
          {receivedRequests.length === 0 ? (
            <div className="text-center text-gray-400">
              No received requests found.
            </div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {receivedRequests.map((req) => (
                <div
                  key={req.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 bg-gray-800/30 rounded-2xl hover:bg-gray-800/50 transition-colors"
                >
                  <div className="flex items-start sm:items-center space-x-4 w-full sm:w-auto">
                    <div className="p-3 rounded-xl shrink-0 bg-red-500/20">
                      <ArrowDownCircle className="w-5 h-5 text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium">
                        From: {req.requesterUsername}
                      </p>
                      <p className="text-gray-400 text-sm">
                        Amount: {req.amount.toFixed(2)} USDC
                      </p>
                      <p className="text-gray-500 text-xs">{req.timestamp}</p>
                    </div>
                  </div>
                  <div className="mt-4 sm:mt-0 text-left sm:text-right space-y-2 w-full sm:w-auto">
                    {req.status === "pending" ? (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleOpenPayModal(req)}
                          disabled={loading}
                          className="bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white px-4 py-2 rounded-xl font-medium hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? "Processing..." : "Pay Now"}
                        </button>
                        <button
                          onClick={() => handleCancelRequest(req)}
                          disabled={loading}
                          className="bg-red-600 text-white px-4 py-2 rounded-xl font-medium hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? "Cancelling..." : "Cancel Request"}
                        </button>
                      </div>
                    ) : (
                      <p
                        className={`text-base sm:text-lg font-bold ${
                          req.status === "completed"
                            ? "text-[#61CE70]"
                            : "text-red-400"
                        }`}
                      >
                        {req.status.charAt(0).toUpperCase() +
                          req.status.slice(1)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showPayModal && selectedPayRequest && (
        <div className="fixed z-[1000] inset-0 bg-black/70 flex items-center justify-center">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-[#61CE70]/30 rounded-3xl p-8 backdrop-blur-md max-w-md w-full relative shadow-2xl shadow-[#61CE70]/10">
            <button
              onClick={() => {
                setShowPayModal(false);
                setSelectedPayRequest(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-[#61CE70] transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
              <CheckCircle className="w-6 h-6 text-[#61CE70]" />
              <span>Confirm Payment</span>
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Wallet className="w-5 h-5 text-[#61CE70]" />
                  <span className="text-gray-400">From:</span>
                </div>
                <span className="text-white font-medium">
                  PayzTap Wallet (
                  <span className="text-[#61CE70]"> {senderUsername} </span>)
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5 text-[#61CE70]" />
                  <span className="text-gray-400">To:</span>
                </div>
                <span className="text-white font-medium">
                  {selectedPayRequest.requesterUsername} (CashTap User)
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-[#61CE70]" />
                  <span className="text-gray-400">Payment Amount:</span>
                </div>
                <span className="text-white">
                  {selectedPayRequest.amount.toFixed(2)} USDC
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-[#61CE70]" />
                  <span className="text-gray-400">Fees (1%):</span>
                </div>
                <span className="text-white">
                  {(selectedPayRequest.amount * 0.01).toFixed(2)} USDC
                </span>
              </div>
              <div className="border-t border-[#61CE70]/20 pt-3 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-[#61CE70]" />
                  <span className="text-white font-medium">Total Amount:</span>
                </div>
                <span className="text-[#61CE70] font-bold">
                  {(selectedPayRequest.amount * 1.01).toFixed(2)} USDC
                </span>
              </div>
            </div>
            <div className="mt-8 flex w-full justify-center space-x-2">
              <button
                onClick={() => {
                  setShowPayModal(false);
                  setSelectedPayRequest(null);
                }}
                className="w-1/2 text-center justify-center py-3 bg-red-600 text-white rounded-2xl hover:bg-red-700 transition-all flex items-center space-x-2"
              >
                <X className="w-4 h-4 bg-white text-red-500 rounded-lg" />
                <span>Cancel</span>
              </button>
              <button
                onClick={confirmPayRequest}
                disabled={loading}
                className="w-1/2 justify-center py-3 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white rounded-2xl hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Send className="w-4 h-4" />
                <span>{loading ? "Processing..." : "Confirm"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentRequestPage;
